import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { IconClose, IconSuccess, IconError, IconInfoNotification, IconWarning } from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';
import { DialogToastVariant, DialogToastDefaultDurationMs } from './constants';
import './style.scss';


const VARIANT_ICON = {
    [DialogToastVariant.SUCCESS]: (props) => <IconSuccess {...props} color={colors.jungleGreen} />,
    [DialogToastVariant.ERROR]: (props) => <IconError {...props} color={colors.pomegranate} />,
    [DialogToastVariant.INFO]: (props) => <IconInfoNotification {...props} color={colors.primaryInfo} />,
    [DialogToastVariant.WARNING]: (props) => <IconWarning {...props} color={colors.orangePeel} />
};

const DialogToast = ({ show, variant, message, onClose, durationMs, animateProgress, showCloseIcon, testId }) => {
    const { t: translate } = useTranslation();

    useEffect(() => {
        if (!show) {
            return undefined;
        }
        const timerId = setTimeout(onClose, durationMs);
        return () => clearTimeout(timerId);
    }, [show, onClose, durationMs]);

    if (!show) {
        return null;
    }

    const renderIcon = VARIANT_ICON[variant];

    return (
        <div className={`dialog-toast dialog-toast--${variant}`} data-testid={testId}>
            <div className='dialog-toast__accent' />
            <div className='dialog-toast__content'>
                <div className='dialog-toast__row'>
                    {renderIcon && <span className='dialog-toast__icon'>{renderIcon({ width: 24, height: 24 })}</span>}
                    <span className='dialog-toast__message'>{message}</span>
                    {showCloseIcon &&
                        <button
                            type='button'
                            className='dialog-toast__close'
                            aria-label={translate('ABB.Powertrain.Frontend.closeBtnLbl')}
                            data-testid={`${testId}-close`}
                            onClick={onClose}
                        >
                            <IconClose width={16} height={16} />
                        </button>
                    }
                </div>
                <div className='dialog-toast__progress'>
                    <div
                        className={`dialog-toast__progress-bar${animateProgress ? ' dialog-toast__progress-bar--animate' : ''}`}
                        data-testid={`${testId}-progress-bar`}
                        style={animateProgress ? { animationDuration: `${durationMs}ms` } : undefined}
                    />
                </div>
            </div>
        </div>
    );
};

DialogToast.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(Object.values(DialogToastVariant)),
    message: PropTypes.node.isRequired,
    durationMs: PropTypes.number,
    animateProgress: PropTypes.bool,
    showCloseIcon: PropTypes.bool,
    testId: PropTypes.string
};

DialogToast.defaultProps = {
    variant: DialogToastVariant.INFO,
    durationMs: DialogToastDefaultDurationMs,
    animateProgress: false,
    showCloseIcon: false,
    testId: 'dialog-toast'
};

export default DialogToast;
